import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminAuthAPI } from "../../api/adminAuth";
import { AdminInvitePreview, AdminRole } from "../../types/adminAuth";

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: "Super Admin",
  manager: "Manager",
};

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => String(searchParams.get("token") || "").trim(),
    [searchParams],
  );

  const [invite, setInvite] = useState<AdminInvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    let ignore = false;
    if (!token) {
      setError("Invite token missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await AdminAuthAPI.previewInvite(token);
        if (ignore) return;
        setInvite(data.invite);
        setFullName(data.invite.fullName || "");
      } catch (e: any) {
        if (ignore) return;
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "This invite is invalid or no longer available.",
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const name = fullName.trim();
    if (!name) {
      setError("Full name is required.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await AdminAuthAPI.acceptInvite({
        token,
        fullName: name,
        password,
      });
      navigate(
        `/login?email=${encodeURIComponent(invite?.email || "")}&invited=1`,
        { replace: true },
      );
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to accept admin invite.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(135deg,#f8fafc,#eef2ff)",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 500,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          padding: 20,
          boxSizing: "border-box",
          boxShadow: "0 10px 28px rgba(15,23,42,0.08)",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22 }}>Accept Admin Invite</h1>
        <p style={{ marginTop: 6, color: "#475569", fontSize: 13 }}>
          Create a strong password, then sign in to the admin panel.
        </p>

        {loading ? <div style={{ marginTop: 12 }}>Loading invite...</div> : null}

        {!loading && invite ? (
          <>
            <div
              style={{
                marginTop: 12,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                borderRadius: 12,
                padding: 12,
                display: "grid",
                gap: 6,
                fontSize: 13,
                color: "#334155",
              }}
            >
              <div>
                <strong>Email:</strong> {invite.email}
              </div>
              <div>
                <strong>Role:</strong> {ROLE_LABELS[invite.role]}
              </div>
              {invite.expiresAt ? (
                <div>
                  <strong>Expires:</strong>{" "}
                  {new Date(invite.expiresAt).toLocaleString()}
                </div>
              ) : null}
            </div>

            <form onSubmit={onSubmit} style={{ display: "grid", gap: 10, marginTop: 12 }}>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                style={inputStyle}
              />
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create password"
                type="password"
                autoComplete="new-password"
                style={inputStyle}
              />
              <input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                type="password"
                autoComplete="new-password"
                style={inputStyle}
              />

              <div style={{ color: "#64748b", fontSize: 12 }}>
                Use at least 10 characters with uppercase, lowercase, number, and
                special character.
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  marginTop: 4,
                  borderRadius: 10,
                  border: "none",
                  background: "#111827",
                  color: "#fff",
                  padding: "10px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                  opacity: submitting ? 0.75 : 1,
                }}
              >
                {submitting ? "Saving..." : "Create Password"}
              </button>
            </form>
          </>
        ) : null}

        {error ? (
          <div
            style={{
              marginTop: 12,
              color: "#b91c1c",
              fontSize: 12,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
              padding: "8px 10px",
            }}
          >
            {error}
          </div>
        ) : null}

        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={textButtonStyle}
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  boxSizing: "border-box",
};

const textButtonStyle: React.CSSProperties = {
  border: "none",
  padding: 0,
  background: "transparent",
  color: "#334155",
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: 12,
};
